import { z } from 'zod';
/**
 * Admin-facing DTOs for the multi-anchor noise filter (CascadeJudgeService L1).
 *
 * Each "anchor" is a small piece of text representing one noise pattern.
 * The cascade computes max(cosine(message, anchor)) and drops messages that
 * score above the threshold.
 */
export const NoiseAnchorSource = z.enum(['manual', 'ai_suggested']);
export const NoiseAnchorItem = z.object({
    id: z.string().uuid(),
    text: z.string(),
    note: z.string().nullable(),
    source: NoiseAnchorSource,
    addedBy: z.string().uuid().nullable(),
    addedAt: z.string().datetime(),
    hitCount: z.number().int(),
    lastHitAt: z.string().datetime().nullable(),
    archived: z.boolean(),
    /** True when this anchor has its embedding stored. Embedding-less rows
     *  exist briefly when the embedding endpoint was unavailable at create
     *  time; they're skipped at L1 until NoiseAnchorService's periodic
     *  backfill catches up. */
    embeddingReady: z.boolean(),
});
export const ListNoiseAnchorsQuery = z.object({
    includeArchived: z.boolean().optional(),
    source: NoiseAnchorSource.optional(),
    limit: z.number().int().min(1).max(500).optional(),
});
export const ListNoiseAnchorsResponse = z.object({
    items: z.array(NoiseAnchorItem),
});
export const CreateNoiseAnchorRequest = z.object({
    text: z.string().min(1).max(4096),
    note: z.string().max(200).optional(),
});
export const BulkCreateNoiseAnchorsRequest = z.object({
    items: z
        .array(z.object({
        text: z.string().min(1).max(4096),
        note: z.string().max(200).optional(),
    }))
        .min(1)
        .max(50),
    /** 'manual' for admin paste, 'ai_suggested' for accepted AI candidates. */
    source: z.enum(['manual', 'ai_suggested']).default('manual'),
});
export const UpdateNoiseAnchorRequest = z.object({
    archived: z.boolean().optional(),
    note: z.string().max(200).nullable().optional(),
});
// ---------------- AI suggestion ----------------
export const SuggestNoiseAnchorsRequest = z.object({
    /** How many raw messages to sample from the lookback window. */
    sampleSize: z.number().int().min(50).max(500).default(200),
    /** How far back to look for sampling. Defaults to 7 days. */
    lookbackHours: z.number().int().min(1).max(720).default(168),
});
export const NoiseAnchorCandidate = z.object({
    text: z.string(),
    /** 14B's one-line reason why this is noise. */
    reason: z.string(),
    /** Highest cosine vs an existing (already-saved) anchor.
     *  High value → adding this is redundant with an existing rule. */
    maxCosineToExisting: z.number(),
    /** Highest cosine vs another candidate already accepted in THIS batch.
     *  High value → this candidate is semantically duplicating an earlier
     *  pick in the same suggestion run. Always 0 for the first candidate. */
    maxCosineWithinBatch: z.number(),
});
export const SuggestNoiseAnchorsResponse = z.object({
    /** How many raw messages were pulled from the lookback window. */
    sampledCount: z.number().int(),
    /** How many of those weren't covered by existing anchors (= sent to 14B). */
    uncoveredCount: z.number().int(),
    /** 14B's picks, deduped vs existing anchors. */
    candidates: z.array(NoiseAnchorCandidate),
});
//# sourceMappingURL=noise-anchor.js.map